import React, { useState, useEffect, useRef } from 'react';
import { 
    StyleSheet, 
    View, 
    FlatList,
    KeyboardAvoidingView,
    TouchableWithoutFeedback,
    Keyboard,
    Dimensions,
    ScrollView,
    SafeAreaView,
    Animated,
    Platform
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';

import TouchableItem from '../../../components/TouchableItem';
import Colors from '../../../constants/Colors';
import DefaultInputMaskedMoney from '../../../components/DefaultInputMaskedMoney';
import DefaultText from '../../../components/DefaultText';
import NavContainer from '../../../components/NavContainer';
import DismissKeyboard from '../../../components/DismissKeyboard';

import * as potentialActions from '../../../store/actions/potential';

const Pillar2Screen = props => {
    const pillar2Data = useSelector(state => state.potential.pillar2);
    const pillar2AmountData = useSelector(state => state.potential.pillar2Amount);
    const [pillar2, setPillar2] = useState(pillar2Data);
    const [pillar2Amount, setPillar2Amount] = useState(pillar2AmountData);
    const [pillar2AmountValid, setPillar2AmountValid] = useState(pillar2AmountData ? true : false);

    const fadeAnim = useRef(new Animated.Value(0)).current;

    const fadeIn = () => {
        // Will change fadeAnim value to 1 in 5 seconds
        Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true
        }).start();
    };

    const fadeOut = () => {
        // Will change fadeAnim value to 0 in 5 seconds
        Animated.timing(fadeAnim, {
            toValue: 0,
            duration: 1000,
            useNativeDriver: true
        }).start();
    };

    useEffect(() => {
        if (pillar2 === true) {
            fadeIn();
        } else {
            fadeOut();
        }
    }, [pillar2])

    const dispatch = useDispatch();

    const inputChangeHandler = async (id, value, valid) => {
        setPillar2Amount(value);
        setPillar2AmountValid(valid);
        dispatch(potentialActions.setPillar2Amount(value));
    };

    const selectionHandler = async (answer) => {
        setPillar2(answer);
        dispatch(potentialActions.setPillar2(answer));
        if (pillar2Data === '') {
            if (answer === false) {
                props.navigation.navigate('Pillar3a');
            } 
        }
    };

    const doneHandler = () => {
        if (pillar2 === false || pillar2AmountValid) {
            props.navigation.navigate('Pillar3a');
        }
    };

    const sView = useRef();

    useEffect(() => {
        sView.current.scrollToEnd({ animated: true });
    })

    return (
        <SafeAreaView style={styles.screen}>
        <ScrollView ref={sView}>
            <KeyboardAvoidingView behavior='position'>
                <DismissKeyboard
                    >
                    <View style={styles.questionContainer}>
                    <View style={styles.questionTextContainer}>
                <DefaultText style={styles.questionText}>Did you buy into your pillar 2 in 2020?</DefaultText>
                <DefaultText style={styles.note}>That's contribution on top of what your employer paid every month.</DefaultText>
                        </View>
                <View style={styles.answerContainer}>
                    <View style={styles.yesNoContainer}>
                        <TouchableItem
                            onSelectItem={() => selectionHandler(false)}
                            style={pillar2 === false ? styles.activeAnswer : styles.passiveAnswer}
                        >
                            <DefaultText
                                style={pillar2 === false ? styles.activeAnswerText : styles.passiveAnswerText}
                            >
                                No
                                </DefaultText>
                        </TouchableItem>
                        <TouchableItem
                            onSelectItem={() => selectionHandler(true)}
                            style={pillar2 === true ? styles.activeAnswer : styles.passiveAnswer}
                        >
                            <DefaultText
                                style={pillar2 === true ? styles.activeAnswerText : styles.passiveAnswerText}
                            >
                                Yes
                            </DefaultText>
                        </TouchableItem>
                        </View>
                    {pillar2 === true && (
                        <Animated.View 
                        style={[
                            styles.inputContainer,
                            {
                              opacity: fadeAnim // Bind opacity to animated value
                            }
                          ]}>
                        <DefaultInputMaskedMoney
                            focus={!(pillar2AmountData > 0)}
                            id='pillar2'
                            title="Pillar 2 buy-in amount:"
                            errorMessage="Please enter a valid amount"
                            placeHolder="10'000"
                            keyboardType='numeric'
                            onInputChange={inputChangeHandler}
                            initialValue={pillar2Amount}
                            initiallyValid={pillar2AmountValid}
                            decimal
                            required
                        />
                        </Animated.View>
                    )}
                </View>

                <NavContainer
                                backShow={true}
                                nextShow={(pillar2 === false || pillar2AmountValid === true)}
                                nextNav={doneHandler}
                                backNav={() => { props.navigation.goBack() }}
                            />

                </View>
                </DismissKeyboard>
            </KeyboardAvoidingView>
        </ScrollView>
        </SafeAreaView>
    );
};

export const pillar2ScreenOptions = navigationData => {
    return {
        title: "Pillar 2",
        headerLeft: () =>
            null
    }
};

const styles = StyleSheet.create({
    screen: {
        flex: 1,
        backgroundColor: Colors.backColor,
        padding: 10,
        alignItems: Platform.OS === 'web' ? 'center' : 'stretch',
    },
    questionContainer: {        
        padding: 10,
        minHeight: Dimensions.get('window').height * 0.87,
        justifyContent: Platform.OS === 'web' ? 'center' : 'flex-end',
        maxWidth: 600,
        minWidth: (Platform.OS === 'web' && Dimensions.get('window').width > 600) ? 600 : 0,
    },
    note: {        
        paddingHorizontal: 10,
        marginBottom: 2,
        fontFamily: 'lato'
    },
    inputContainer: {
        flex: 1,
        marginTop: 20
    },
    yesNoContainer: {
        justifyContent: 'space-between',
        flexDirection: 'row',
    },
    questionText: {
        fontFamily: 'lato-bold',
        color: Colors.darkBlue,
        fontSize: 20,
        marginBottom: 10,
        paddingHorizontal: 10
    },
    questionTextContainer: {
        marginBottom: 40,
    },
    answerContainer: {
        justifyContent: 'space-between',
        // minHeight: 200,
        padding: 10,
    },
    passiveAnswer: {
        borderRadius: 5,
        borderWidth: 0.5,
        borderColor: Colors.darkGreen,
        minHeight: 50,
        width: (Platform.OS === 'web' && Dimensions.get('window').width > 600) ? 250 : (Dimensions.get('window').width - 100) / 2,
        alignItems: 'center',
        justifyContent: 'center'
    },
    activeAnswer: {
        borderRadius: 5,
        borderWidth: 0.5,
        borderColor: Colors.darkGreen,
        backgroundColor: Colors.buttonGreen,
        minHeight: 50,
        width: (Platform.OS === 'web' && Dimensions.get('window').width > 600) ? 250 : (Dimensions.get('window').width - 100) / 2,
        alignItems: 'center',
        justifyContent: 'center'
    },
    activeAnswerText: {
        fontFamily: 'lato-bold',
        fontSize: 20,
        color: Colors.darkGreen,
    },
    passiveAnswerText: {
        fontFamily: 'lato-bold',
        fontSize: 16,
        color: Colors.darkGreen,
    },
});

export default Pillar2Screen;